import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllLanguages,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  Language,
} from "../../services/api";
import LoadingState from "../Common/LoadingState";

const LanguageConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const [formData, setFormData] = useState({
    language: "",
    language_code: "",
    google_voice_id: "",
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoading(true);
      const data = await getAllLanguages();
      setLanguages(data);
    } catch (err: any) {
      console.error("Error fetching languages:", err);
      setError(err.message || "Failed to load languages");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      language: "",
      language_code: "",
      google_voice_id: "",
    });
    setEditingLanguage(null);
  };

  const handleAddLanguage = () => {
    setShowAddModal(true);
    resetForm();
  };

  const handleEditLanguage = (language: Language) => {
    setEditingLanguage(language);
    setFormData({
      language: language.language,
      language_code: language.language_code,
      google_voice_id: language.google_voice_id,
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.language ||
      !formData.language_code ||
      !formData.google_voice_id
    ) {
      setError("All fields are required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingLanguage) {
        // Update existing language
        await updateLanguage(editingLanguage.id, formData);
        setSuccess(`Language "${formData.language}" updated successfully!`);
      } else {
        // Create new language
        await createLanguage(formData);
        setSuccess(`Language "${formData.language}" added successfully!`);
      }

      // Refresh languages list
      fetchLanguages();
      setShowAddModal(false);
      resetForm();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      console.error("Error saving language:", err);
      setError(err.message || "Failed to save language");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLanguage = async (id: number, languageName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${languageName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await deleteLanguage(id);
      setSuccess(`Language "${languageName}" deleted successfully!`);

      // Refresh languages list
      fetchLanguages();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err: any) {
      console.error("Error deleting language:", err);
      setError(err.message || "Failed to delete language");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState message="Loading languages..." fullPage={true} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#140d0c]">
          Language Configuration
        </h1>
        <button
          onClick={() => navigate("/admin/agents")}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors flex items-center gap-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Agents
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-700 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium text-[#140d0c]">
            Supported Languages
          </h2>
          <button
            onClick={handleAddLanguage}
            className="px-4 py-2 bg-[#ffcc33] text-[#140d0c] rounded hover:bg-[#ffcc33]/90 transition-colors flex items-center gap-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Language
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-[#e7e2d3]">
            <thead>
              <tr className="bg-[#f2efe3]">
                <th className="py-3 px-4 text-left border-b border-[#e7e2d3] text-[#140d0c]">
                  Language
                </th>
                <th className="py-3 px-4 text-left border-b border-[#e7e2d3] text-[#140d0c]">
                  Language Code
                </th>
                <th className="py-3 px-4 text-left border-b border-[#e7e2d3] text-[#140d0c]">
                  Google Voice ID
                </th>
                <th className="py-3 px-4 text-center border-b border-[#e7e2d3] text-[#140d0c]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {languages.map((language) => (
                <tr
                  key={language.id}
                  className="border-b border-[#e7e2d3] hover:bg-[#f8f7f3]"
                >
                  <td className="py-3 px-4 text-[#140d0c]">
                    {language.language}
                  </td>
                  <td className="py-3 px-4 text-[#140d0c]">
                    {language.language_code}
                  </td>
                  <td className="py-3 px-4 text-[#140d0c]">
                    {language.google_voice_id}
                  </td>
                  <td className="py-3 px-4 flex justify-center gap-2">
                    <button
                      onClick={() => handleEditLanguage(language)}
                      className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteLanguage(language.id, language.language)
                      }
                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {languages.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-gray-500">
                    No languages configured. Add a language to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Language Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-[#140d0c]">
                {editingLanguage ? "Edit Language" : "Add New Language"}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="language"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Language Name
                </label>
                <input
                  type="text"
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffcc33]"
                  placeholder="e.g., English, Hindi"
                  required
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="language_code"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Language Code
                </label>
                <input
                  type="text"
                  id="language_code"
                  name="language_code"
                  value={formData.language_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffcc33]"
                  placeholder="e.g., en-IN, hi-IN"
                  required
                />
              </div>

              <div className="mb-6">
                <label
                  htmlFor="google_voice_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Google Voice ID
                </label>
                <input
                  type="text"
                  id="google_voice_id"
                  name="google_voice_id"
                  value={formData.google_voice_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ffcc33]"
                  placeholder="e.g., en-IN-Wavenet-E"
                  required
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 bg-[#ffcc33] text-[#140d0c] rounded hover:bg-[#ffcc33]/90 transition-colors ${
                    saving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {saving
                    ? "Saving..."
                    : editingLanguage
                    ? "Update Language"
                    : "Add Language"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {saving && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <LoadingState
            size="medium"
            padding="large"
            message="Saving changes..."
            fullPage={false}
          />
        </div>
      )}
    </div>
  );
};

export default LanguageConfiguration;
